/* eslint-disable max-len */
import { useFormikContext } from 'formik';
import { ReactElement } from 'react';
import FormCheckboxList from 'src/components/FormInputs/FormCheckboxList/FormCheckboxList';
import FormDropdown from 'src/components/FormInputs/FormDropdown/FormDropdown';
import FormRadioList from 'src/components/FormInputs/FormRadioList/FormRadioList';
import { useAppSelector } from 'src/redux/hooks';
import {
  selectStudentInterventionDataEntryMode,
  selectStudentInterventionFormViewModel,
  selectStudentInterventionStudentIntervention,
} from 'src/redux/slices/studentInterventionSlice';
import { DataEntryMode } from 'src/types/DataEntryMode';
import { FormRadioListOrientation } from 'src/types/propTypes/FormRadioListPropTypes';
import { PanelType } from '../../../types/PanelType';
import { StudentInterventionModel } from '../../../types/StudentInterventionModel';
import { StudentInterventionValidationEditSchema } from '../../../types/Validation/StudentInterventionValidationEditSchema';
import { StudentInterventionValidationSchema } from '../../../types/Validation/StudentInterventionValidationSchema';
import { isStateNonAdmin } from '../../../utilities/userUtilities';
import FormDateSelector from '../../FormInputs/FormDateSelector/FormDateSelector';
import FormTextbox from '../../FormInputs/FormTextbox/FormTextbox';
import Panel from '../../Panel/Panel';
import { MAX_DATE } from 'src/types/Constants';

const StudentIntervention_InterventionDetailsPanel = (): ReactElement => {
  const { values } = useFormikContext<StudentInterventionModel>();
  const dataEntryMode: DataEntryMode = useAppSelector(
    selectStudentInterventionDataEntryMode
  );

  const formViewModel = useAppSelector(selectStudentInterventionFormViewModel);
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  const studentIntervention = useAppSelector(
    selectStudentInterventionStudentIntervention
  );

  // Determine if interventionReferralDate should be disabled
  const isInterventionReferralDateDisabled = (): boolean => {
    // Get the current school year from the form values (same as LEA & School Information panel)
    const currentSchoolYear = values.schoolYear;

    // Only disable when it's current school year AND page is in EDIT mode
    return (
      dataEntryMode === DataEntryMode.EDIT &&
      currentSchoolYear === formViewModel.defaultSchoolYear.split('-').pop()
    );
  };

  return (
    <>
      <Panel panelType={PanelType.INFO} heading="Intervention Details">
        <div className="field-row field-row-panel">
          <div>
            <FormCheckboxList
              displayName="Intervention Criteria"
              field="interventionCriteria"
              options={formViewModel.interventionCriteriaOptions ?? []}
              validationSchema={StudentInterventionValidationSchema}
              disabled={true}
            />
          </div>
          <div className="panel-details">
            <FormDateSelector
              displayName="Intervention Start Date"
              field="interventionStartDate"
              validationSchema={
                dataEntryMode === 'new'
                  ? StudentInterventionValidationSchema
                  : StudentInterventionValidationEditSchema
              }
              maxDate={MAX_DATE}
              isClearable
            />
            <br />
            {dataEntryMode === DataEntryMode.EDIT && (
              <FormTextbox
                displayName="Number of Suspension days"
                field="numberofSuspensionDays"
                validationSchema={StudentInterventionValidationSchema}
                disabled={true}
              />
            )}
            <br />
            <FormRadioList
              displayName="Tier Placement"
              field="tierPlacement"
              options={formViewModel.tierPlacementOptions ?? []}
              optionOrientation={FormRadioListOrientation.Horizontal}
              validationSchema={StudentInterventionValidationSchema}
              disabled={isStateNonAdmin()}
            />
          </div>
        </div>

        <div className="field-row field-row-panel-details">
          <FormDateSelector
            displayName="Intervention Referral Date"
            field="interventionReferralDate"
            placeholderText="mm/dd/yyyy"
            disabled={isInterventionReferralDateDisabled()}
            validationSchema={StudentInterventionValidationSchema}
            maxDate={MAX_DATE}
            isClearable
          />
          <FormDateSelector
            displayName="Intervention Decision Date"
            field="interventionDecisionDate"
            placeholderText="mm/dd/yyyy"
            disabled={isStateNonAdmin()}
            maxDate={MAX_DATE}
            isClearable
          />
        </div>
        <div className="field-row field-row-panel-details">
          <FormDropdown
            displayName="Intervention Success"
            field="interventionSuccess"
            options={formViewModel.interventionSuccessOptions ?? []}
            validationSchema={StudentInterventionValidationSchema}
            disabled={isStateNonAdmin()}
            isClearable
          />
          <FormDateSelector
            displayName="Documented Review Date"
            field="documentedReviewDate"
            placeholderText="mm/dd/yyyy"
            disabled={isStateNonAdmin()}
            maxDate={MAX_DATE}
            isClearable
          />
          <FormDateSelector
            displayName="Cumulative Documented Review Date"
            field="cumulativeDocumentedReviewDate"
            placeholderText="mm/dd/yyyy"
            disabled={isStateNonAdmin()}
            maxDate={MAX_DATE}
            isClearable
          />
        </div>
      </Panel>
    </>
  );
};

export default StudentIntervention_InterventionDetailsPanel;
